/// <amd-module name="Artifacts/Maven/redux/Sagas" />
import { MavenMinimalPackageDetails, MavenPomGav } from "Artifacts/Maven/Contracts/Maven";
import { MavenActionTypes } from "Artifacts/Maven/redux/Actions";
import * as Res from "Artifacts/Maven/Resources";
import { IFeedMavenDataService } from "Artifacts/Maven/services/MavenDataService";
import { PackageActions } from "Artifacts/Package/redux/Actions";
import { BatchGenerator, IBatchIterator } from "Artifacts/Package/services/BatchHandling";
import { PerfScenarios } from "Artifacts/Root/Constants";
import { FeedPerformanceHelper } from "Artifacts/Root/helpers/FeedPerformanceHelper";
import {
  IDeleteActionPayload,
  IPromoteActionPayload,
  IRestoreActionPayload,
  IUpstreamOverrideActionPayload,
} from "Artifacts/Root/Protocols/types/ICommandActionPayloads";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { call, getContext, put, takeEvery } from "Artifacts/Root/Saga";
import { IPackageVersionIdentity } from "Artifacts/Root/types/IPackageVersionIdentity";
import {
  UpstreamingBehavior,
  UpstreamVersionVisibility,
} from "Artifacts/Root/types/PackagingShared";
import { SagaIterator } from "redux-saga";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssScenario } from "VSS/Platform/Performance";

export function* rootSaga(): SagaIterator {
  yield takeEvery(MavenActionTypes.PermanentDelete, permanentDelete);
  yield takeEvery(MavenActionTypes.Promote, promote);
  yield takeEvery(MavenActionTypes.Restore, restore);
  yield takeEvery(MavenActionTypes.SoftDelete, softDelete);
  yield takeEvery(MavenActionTypes.SetUpstreamOverride, setUpstreamOverride);
}

export function* promote(
  action: ActionWithPayload<MavenActionTypes.Promote, IPromoteActionPayload>,
): Generator {
  try {
    yield put(RootActions.promoteCommandStarted());
    const { feed, versions, viewId, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const dataService = pageContext.getService<IFeedMavenDataService>("IFeedMavenDataService");

    const iterator: IBatchIterator<MavenMinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        const { groupId, artifactId, version } = toPomGav(v);
        return {
          group: groupId,
          artifact: artifactId,
          version,
        } as MavenMinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call(
      [dataService, dataService.batchPromotePackages],
      projectId,
      feed.id,
      viewId,
      iterator,
    );

    // retrieve the view so the name can be applied on the selected versions.
    const view = feed.views.find((v) => v.id === viewId);

    yield put(RootActions.promoteCommandCompleted());
    yield call(onSuccessCallback, versions, view?.name);
  } catch (error) {
    yield put(RootActions.promoteCommandFailed(error.message));
  }
}

export function* restore(
  action: ActionWithPayload<MavenActionTypes.Restore, IRestoreActionPayload>,
): Generator {
  yield put(RootActions.restoreCommandStarted());
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionRestore
        : PerfScenarios.MultiplePackageVersionsRestoredToFeed,
    );

    const dataService = pageContext.getService<IFeedMavenDataService>("IFeedMavenDataService");

    const iterator: IBatchIterator<MavenMinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        const { groupId, artifactId, version } = toPomGav(v);
        return {
          group: groupId,
          artifact: artifactId,
          version,
        } as MavenMinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchRestoreToFeed], projectId, feed.id, iterator);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.restoreCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.restoreCommandFailed(error.message));
  }
}

export function* permanentDelete(
  action: ActionWithPayload<MavenActionTypes.PermanentDelete, IDeleteActionPayload>,
): Generator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    yield put(RootActions.permDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionPermanentDelete
        : PerfScenarios.MultiplePackageVersionsPermanentDeleted,
    );

    const dataService = pageContext.getService<IFeedMavenDataService>("IFeedMavenDataService");
    const iterator: IBatchIterator<MavenMinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        const { groupId, artifactId, version } = toPomGav(v);
        return {
          group: groupId,
          artifact: artifactId,
          version,
        } as MavenMinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchPermanentlyDelete], projectId, feed.id, iterator);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.permDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.permDeleteCommandFailed(error.message));
  }
}

export function* softDelete(
  action: ActionWithPayload<MavenActionTypes.SoftDelete, IDeleteActionPayload>,
): Generator {
  try {
    yield put(RootActions.softDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const dataService = pageContext.getService<IFeedMavenDataService>("IFeedMavenDataService");

    const iterator: IBatchIterator<MavenMinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        const { groupId, artifactId, version } = toPomGav(v);
        return {
          group: groupId,
          artifact: artifactId,
          version,
        } as MavenMinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchDeletePackageVersions], projectId, feed.id, iterator);

    yield put(RootActions.softDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    yield put(RootActions.softDeleteCommandFailed(error.message));
  }
}

export function* setUpstreamOverride(
  action: ActionWithPayload<MavenActionTypes.SetUpstreamOverride, IUpstreamOverrideActionPayload>,
): Generator {
  try {
    yield put(PackageActions.upstreamOverrideCommandStarted());
    const { feed, packageName, allowExternalVersions, onSuccessCallback } = action.payload;

    const behavior = {
      versionsFromExternalUpstreams: allowExternalVersions
        ? UpstreamVersionVisibility.AllowExternalVersions
        : UpstreamVersionVisibility.Auto,
    } as UpstreamingBehavior;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;

    const dataService = pageContext.getService<IFeedMavenDataService>("IFeedMavenDataService");

    const projectId = feed.project ? feed.project.id : "";

    yield call(
      [dataService, dataService.setUpstreamingBehavior],
      feed.id,
      packageName,
      behavior,
      projectId,
    );

    yield put(PackageActions.upstreamOverrideCommandCompleted(allowExternalVersions));
    yield call(onSuccessCallback);
  } catch (error) {
    yield put(PackageActions.upstreamOverrideCommandFailed(error.message));
  }
}

function toPomGav(packageVersion: IPackageVersionIdentity): MavenPomGav {
  // Maven packages are of the form groupId:artifactId. Anything other than that are rejected.
  const nameParts = (packageVersion.name || "").split(":");
  if (nameParts.length !== 2) {
    throw new Error(Res.MavenOverview_InvalidPackageError);
  }

  return <MavenPomGav>{
    groupId: nameParts[0],
    artifactId: nameParts[1],
    version: packageVersion.version,
  };
}
